import { ReactNode, useContext } from 'react';

import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import NavLogo, { NavLogoProps } from '../Logo';
import { NAV_HEIGHT } from '../sizes';
import { TopNavMenu } from './TopNavMenu';
import {
  Badge,
  Box,
  Button,
  ColorsV2,
  Flex,
  getToken,
  IconButton,
  Pane,
  Skeleton,
  spaces,
  Tooltip,
  useBreakpoint,
} from '@electricjs/arc';
import { TopNavMenuItem } from './types';
import { NavLink } from 'react-router-dom';
import { CopilotKitAIChatDrawer } from '@/components/AIChat/CopilotKitAIChatDrawer';
import { CopilotKitAgentContext } from '@/components/CopilotKitProvider/CopilotKitAgentContext';
import { useAppDispatch, useAppSelector } from '@/redux/store';
import { toggleDrawer, closeDrawer } from '@/redux/slices/aiChatDrawerSlice';
import { useAIChatFeatureFlags } from '@/hooks/useAIChatFeatureFlags';
import { useSetupAssistantUpgradeState } from '@/hooks/aiChat/useSetupAssistantUpgradeState';
import { appendUpgradeNudgeMessage } from '@/helpers/aiChat/messages';
import { getLocalizedSupportUrl } from '@/helpers/getLocalizedSupportUrl';

const StyledTopNav = styled(Pane)`
  z-index: 2;
  height: ${NAV_HEIGHT};
  min-height: ${NAV_HEIGHT};
  padding: ${props => (props.$md ? '0 2.5rem' : '0 1rem')};
  border-radius: unset;
  flex-wrap: nowrap;

  justify-content: ${props =>
    props.$md &&
    !props.$shouldShowLogo &&
    !props.$infoElement &&
    !props.$pageTitleElement
      ? 'flex-end'
      : 'space-between'};

  background: ${getToken(ColorsV2.BACKGROUND)};
  border-bottom: solid 1px ${getToken(ColorsV2.GRAY_LIGHT)};
`;

export type TopNavProps = {
  /** Shows the skeleton loading state if true */
  isNavLoading?: boolean;
  /** Requires a logoElement and logoLinkProps (href & optional target) */
  logoProps: NavLogoProps;
  /** Callback for when you click the mobile menu */
  onMobileMenuClick?: () => void;
  /** Hides the mobile menu icon in the event that you do not have a side nav */
  hideMobileMenu?: boolean;
  /** Top right nav "name", usually the user's name */
  navMenuName: string;
  /** Menu items for the top right nav menu */
  menuItems: TopNavMenuItem[];
  /** Hides the knowledge base link */
  hideKnowledgeBaseLink?: boolean;
  /** Custom knowledge base URL */
  knowledgeBaseURL?: string;
  /** Manually show/hide the logo, useful for "full page" views */
  shouldShowLogo?: boolean;
  /** Link for logout button, use either this or the "logoutOnClick" */
  logoutLink?: string;
  /** On click callback for logout button, use either this or the "logoutLink" */
  logoutOnClick?: () => void;
  /** Optional element to display on the left side of the Top Nav */
  infoElement?: JSX.Element;
  /** Optional notifications component to display on the Right side of the Top Nav */
  notificationsElement?: JSX.Element;
  // Optional search component to display on the left side of the Top Nav
  searchElement?: JSX.Element;
  // Optional count of tasks to display on the tasks icon
  taskCount?: number;
  // Menu detail section for the new version- usually the organization name and other relevant details
  menuDetailSection?: JSX.Element;
  /** Page title element rendered in the left zone on tablet+ screens */
  pageTitleElement?: ReactNode;
};

const TopNav = ({
  isNavLoading,
  logoProps,
  onMobileMenuClick,
  hideMobileMenu = false,
  navMenuName,
  menuItems,
  hideKnowledgeBaseLink = false,
  knowledgeBaseURL,
  shouldShowLogo = false,
  logoutLink,
  logoutOnClick,
  infoElement,
  notificationsElement,
  searchElement,
  taskCount,
  menuDetailSection,
  pageTitleElement,
}: TopNavProps) => {
  const { t } = useTranslation([
    'common',
    'translation',
    'support-center',
    'ai-chat',
  ]);
  const { md, lg } = useBreakpoint();
  const dispatch = useAppDispatch();
  const isAIChatDrawerOpen = useAppSelector(state => state.aiChatDrawer.isOpen);
  const {
    showTopNavAskAI,
    ithEmbeddedAiChat,
    canAccessGigawattForSetupAssistant,
  } = useAIChatFeatureFlags();
  // Not scoped via useCopilotKitAgentContext — this component renders even when
  // canAccessGigawattForSetupAssistant is false (outside CopilotKitAgentProvider),
  // where that hook would throw. Mirrors useGigawattPrompt.ts's null-safe pattern.
  const copilotKitAgentContext = useContext(CopilotKitAgentContext);

  // Shared with CopilotKitAgentContext's guarded send and the setup
  // assistant's Upgrade CTA so all three call sites agree on completion state.
  const { setupFullyComplete } = useSetupAssistantUpgradeState();

  const handleAskAIClick = () => {
    const willOpen = !isAIChatDrawerOpen;

    dispatch(toggleDrawer());

    if (!willOpen || !setupFullyComplete || !copilotKitAgentContext?.agent) {
      return;
    }

    // Shares UPGRADE_NUDGE_MESSAGE_ID with CopilotKitAgentContext's guarded
    // send so whichever path shows it first, the other doesn't append a duplicate.
    appendUpgradeNudgeMessage(
      copilotKitAgentContext.agent,
      t('ai-chat:upgradeNudgeMessage')
    );
  };

  const onKnowledgeBaseClick = () => {
    const url = knowledgeBaseURL ?? 'https://support.electric.ai/'; // defaults to main support page

    const newWindow = window.open();
    if (newWindow) {
      newWindow.opener = null; // works like the rel="noreferrer noopener" of the anchor tag
      newWindow.location = getLocalizedSupportUrl(url); // loads the page after the "opener" reference is cleared
    }
  };

  const mobileHamburgerMenuAndLogo = !md ? (
    hideMobileMenu ? (
      <NavLogo {...logoProps} />
    ) : (
      <>
        <IconButton
          id="topnav-hamburger-button"
          icon="menu"
          onClick={onMobileMenuClick}
          intent="neutral"
        />
        <NavLogo {...logoProps} />
      </>
    )
  ) : null;

  /* Show basic nav while nav states are loading */
  if (isNavLoading) {
    return (
      <nav aria-label="Admin" style={{ width: '100%' }}>
        <StyledTopNav
          withPadding
          vertical="center"
          id="topnav"
          testId="topnav"
          $md={md}
          $shouldShowLogo={shouldShowLogo}>
          {/* Mobile hamburger menu + logo */}
          {mobileHamburgerMenuAndLogo}
          {/* Logo */}
          {shouldShowLogo && md ? <NavLogo {...logoProps} /> : null}
          <Flex>
            {!hideKnowledgeBaseLink ? (
              <span style={{ marginRight: '0.8rem' }}>
                <Skeleton
                  circle={!md}
                  count={1}
                  width={!md ? '4rem' : '14.5rem'}
                  height={!md ? '4rem' : '3.4rem'}
                />
              </span>
            ) : null}
            <Skeleton
              circle={!md}
              count={1}
              width={!md ? '4rem' : '14.5rem'}
              height={!md ? '4rem' : '3.4rem'}
            />
          </Flex>
        </StyledTopNav>
      </nav>
    );
  }

  return (
    <nav aria-label="Admin" style={{ width: '100%' }}>
      <StyledTopNav
        withPadding
        vertical="center"
        id="topnav"
        testId="topnav"
        $md={md}
        $shouldShowLogo={shouldShowLogo}
        $infoElement={infoElement}
        $pageTitleElement={pageTitleElement}>
        {/* Mobile hamburger menu + logo */}
        {mobileHamburgerMenuAndLogo}

        {/* Logo */}
        {shouldShowLogo && md ? <NavLogo {...logoProps} /> : null}

        {/* Page title (tablet+ only) */}
        {md && pageTitleElement ? (
          <Box flex="1" minWidth="0" overflow="hidden" mr={spaces.space_m}>
            {pageTitleElement}
          </Box>
        ) : null}

        {/* Right side top nav buttons */}
        <>
          {/* Info element for site wide warnings - Beta chip, outages, etc */}
          {infoElement ? infoElement : null}

          <Flex
            justifyContent="flex-end"
            alignItems="center"
            flexShrink={md && pageTitleElement ? 0 : undefined}
            width={md && pageTitleElement ? undefined : '100%'}
            gap={spaces.space_m}>
            {/* Search Icon */}
            <div>{searchElement}</div>

            {showTopNavAskAI && md && (
              <Box>
                <Button
                  id="ask-ai"
                  iconBefore="sparkles"
                  variant="outline"
                  onClick={handleAskAIClick}>
                  {t('translation:nav.askAI')}
                </Button>
              </Box>
            )}

            {/* Knowledge Base Link */}
            {!hideKnowledgeBaseLink && md ? (
              <Tooltip
                content={t('support-center:cards.knowledgeBase.title')}
                placement="bottom">
                <div>
                  <IconButton
                    icon="question-circle"
                    onClick={onKnowledgeBaseClick}
                    id="user-menu-knowledge"
                    intent="primary"
                    iconSize="medium"
                  />
                </div>
              </Tooltip>
            ) : null}

            <Tooltip content={t('translation:nav.tasks')} placement="bottom">
              <div style={{ position: 'relative' }}>
                <NavLink to="/tasks">
                  <IconButton
                    icon="check-circle"
                    id="user-menu-tasks"
                    testId="user-menu-tasks"
                    intent="primary"
                    iconSize="medium"
                  />
                  <Badge
                    size="1.8rem"
                    variant="legal-bold"
                    position="absolute"
                    top="0px"
                    left="2.2rem"
                    count={taskCount ?? 0}
                  />
                </NavLink>
              </div>
            </Tooltip>

            {/* Notifications */}
            {!!notificationsElement && (
              <Flex shrink={0}>{notificationsElement}</Flex>
            )}

            {/* Right-most menu */}
            <TopNavMenu
              md={Boolean(md)}
              navMenuName={navMenuName}
              menuItems={
                // When in mobile, move the Knowledge Base link to the end of the menu instead of an icon in the top bar.
                md
                  ? menuItems
                  : [
                      ...menuItems,
                      {
                        text: t('support-center:cards.knowledgeBase.title'),
                        icon: 'question-circle',
                        callbackFn: onKnowledgeBaseClick,
                      },
                    ]
              }
              logoutLink={logoutLink}
              logoutOnClick={logoutOnClick}
              menuDetailSection={menuDetailSection}
            />
          </Flex>
        </>
      </StyledTopNav>

      {/* AI Chat Drawer — used below lg, or on lg+ when the embedded panel flag is off */}
      {/* TODO(TF-677): use hasAnyGigawattAccess once free-plan setup-assistant access is no longer temporary */}
      {canAccessGigawattForSetupAssistant && (!lg || !ithEmbeddedAiChat) && (
        <CopilotKitAIChatDrawer
          visible={isAIChatDrawerOpen}
          onClose={() => dispatch(closeDrawer())}
        />
      )}
    </nav>
  );
};

export { TopNav };
